import {clearProjectPreferences} from './projectPreferences';
import {debugLog} from './debug';

/**
 * Everything that has to be forgotten when a session ends.
 *
 * Signing out on the web does not reload the page, so any state living outside Redux — localStorage
 * preferences, module-level HTTP caches — survives into the *next* account's session unless it is
 * explicitly dropped. That is how one account's project id ended up on the next account's requests,
 * which the worker answered with `403 You do not own this project`.
 *
 * The registry exists to avoid an import cycle: `authThunks` calls `clearSessionScopedCaches()`, and
 * the thunk modules that own a cache register their own reset here. Every one of them already
 * imports `signOut` from `authThunks`, so they cannot be imported from it in turn.
 */

type Cleanup = () => void;

const cleanups = new Set<Cleanup>();

/**
 * Register a module-level cache to be cleared on sign-out. Call at module scope — registration has
 * to have happened before the first sign-out, and the store imports every slice on startup.
 */
export function onSessionCleared(cleanup: Cleanup): void {
  cleanups.add(cleanup);
}

/**
 * Drop every scrap of the outgoing session that is not in Redux. The store itself is reset by the
 * root reducer in `store.ts` on `auth/signOut/fulfilled`.
 */
export function clearSessionScopedCaches(): void {
  clearProjectPreferences();

  for (const cleanup of cleanups) {
    try {
      cleanup();
    } catch (error) {
      debugLog('[sessionCleanup] A cleanup threw (ignored):', error);
    }
  }

  debugLog('[sessionCleanup] Session-scoped caches cleared');
}
